import { Chip, Panel, Stat, Stats } from "./ui";
import { BTN, code, TONE } from "./lib";

const sec = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);

function uptime(s = 0) {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return d ? `${d} kun ${h} soat` : h ? `${h} soat ${m} daq` : `${m} daq`;
}

/** Tizim holati — `/api/llm_status` dan qisqa ko'rinish; to'liq diagnostika `/llm_status` da. */
export default function SystemTab({ status, error }) {
  if (error) return <div className="text-xs text-rose-300">{error}</div>;
  if (!status) return <div className="py-10 text-center text-xs text-sky-400/60">yuklanmoqda...</div>;

  const st = status.turn_stats ?? {};
  const qa = status.quality ?? {};
  const vec = status.vectors ?? {};
  const m = status.machine ?? {};
  const steps = Object.entries(st.by_step ?? {}).sort((a, b) => b[1] - a[1]);
  const stepMax = steps[0]?.[1] || 1;

  return (
    <div className="space-y-5">
      <Stats>
        <Stat
          label="Model"
          value={<span className="text-sm">{status.model}</span>}
          hint={`${status.backend} · ${status.online ? "ishlayapti" : "javob bermayapti"}`}
          tone={status.online ? "" : "text-rose-300"}
        />
        <Stat label="Mediana" value={st.count ? sec(st.median_ms) : "—"} hint={st.count ? `p90 ${sec(st.p90_ms)} · max ${sec(st.max_ms)}` : "so'rov yo'q"} />
        <Stat label="Sekin" value={st.slow ?? 0} hint={`${st.count ?? 0} tadan`} tone={st.slow ? "text-amber-300" : ""} />
        <Stat
          label="Vektorlar"
          value={vec.count?.toLocaleString("ru-RU") ?? 0}
          hint={vec.error || vec.backend}
          tone={vec.ready ? "" : "text-rose-300"}
        />
        <Stat label="Bilim bo'laklari" value={qa.kb_chunks?.toLocaleString("ru-RU") ?? "—"} hint={`${qa.documents ?? 0} hujjat`} />
        <Stat label="Javobsiz" value={qa.unanswered ?? 0} tone={qa.unanswered ? "text-amber-300" : ""} />
      </Stats>
      {qa.error && <div className="text-[11px] text-rose-300">Baza: {qa.error}</div>}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Panel title="Bosqichlar (jami vaqt ulushi)">
          {steps.length === 0 ? (
            <p className="text-sky-400/60">Server ishga tushgandan beri so'rov yo'q</p>
          ) : (
            <div className="space-y-1.5">
              {steps.map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 truncate">{k}</span>
                  <span className="h-1.5 flex-1 overflow-hidden rounded bg-[#0b2a50]">
                    <span className="block h-full rounded bg-sky-400" style={{ width: `${(v / stepMax) * 100}%` }} />
                  </span>
                  <span className="w-16 shrink-0 text-right font-mono text-[10px] text-sky-400/70">{sec(v)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Qurilma">
          {m.note ? (
            <p className="text-sky-400/60">{m.note}</p>
          ) : (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
              {m.cpu_percent != null && <><dt>CPU</dt><dd>{m.cpu_percent}% · {m.cpu_count} yadro</dd></>}
              {m.ram && <><dt>Xotira</dt><dd>{m.ram.used_gb} / {m.ram.total_gb} GB</dd></>}
              {m.swap && <><dt>Swap</dt><dd className={m.swap.percent > 50 ? "text-amber-300" : ""}>{m.swap.used_gb} GB</dd></>}
              {m.disk && <><dt>Disk</dt><dd>{m.disk.free_gb} GB bo'sh</dd></>}
            </dl>
          )}
          <p className="mt-1.5 text-[10px] text-sky-400/60">Ishlash vaqti {uptime(m.uptime_s)}</p>
        </Panel>
      </div>

      {status.errors?.length > 0 && (
        <Panel title="LLM xatolari">
          <ul className="space-y-0.5 font-mono text-[10px]">
            {status.errors.slice(0, 10).map((e, i) => (
              <li key={i} className="text-rose-300">
                {e.code} {e.endpoint} {e.ms ? `· ${sec(Math.round(e.ms))}` : ""}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {qa.top_unanswered?.length > 0 && (
        <Panel title="Ko'p so'ralgan javobsiz savollar">
          <ul className="space-y-1">
            {qa.top_unanswered.map((u, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-mono text-amber-300">{u.count}×</span>
                <span className="min-w-0 flex-1 truncate">{u.question}</span>
                <span className="text-sky-400/60">{u.reason}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {status.turns?.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-white">Oxirgi so'rovlar</h2>
          <div className="overflow-x-auto rounded-xl border border-sky-800/40">
            <table className="w-full min-w-[560px] text-left text-[11px]">
              <thead className="bg-[#041228] text-sky-400/70">
                <tr>
                  <th className="px-3 py-2 font-medium">Savol</th>
                  <th className="px-3 py-2 font-medium">Kod</th>
                  <th className="px-3 py-2 font-medium">Vaqt</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {status.turns.slice(0, 15).map((t, i) => (
                  <tr key={i} className="border-t border-sky-900/40 bg-[#030f24]/60">
                    <td className="max-w-[320px] truncate px-3 py-2 text-slate-200">{t.question}</td>
                    <td className="px-3 py-2 font-mono text-sky-200">{t.code ? code(t.code) : "—"}</td>
                    <td className={`px-3 py-2 font-mono ${t.slow ? "text-amber-300" : "text-sky-300/80"}`}>{sec(t.total_ms)}</td>
                    <td className="px-3 py-2">
                      {t.error ? <Chip cls={TONE.bad}>xato</Chip> : t.warning ? <Chip cls={TONE.warn}>ogohlantirish</Chip> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <a href="/llm_status" className={`${BTN} inline-block`}>
        To'liq diagnostika
      </a>
    </div>
  );
}
